import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import authService from '../../services/authService';
import { useTranslation } from 'react-i18next';
import { isRoleModuleEnabled, moduleForPath } from '../../utils/roleModuleAccess.js';


const FeatureUnavailable = ({ message }) => (
  <div className="flex items-center justify-center min-h-screen bg-light p-4">
    <div className="text-center w-full">
      <div className="bg-white p-6 sm:p-8 rounded-lg shadow-lg max-w-md mx-auto">
        <div className="text-danger text-4xl sm:text-5xl mb-4">🚫</div>
        <p className="text-danger text-lg sm:text-xl font-semibold mb-2">Feature Not Available</p>
        <p className="text-secondary text-sm sm:text-base mt-2 mb-4">
          {message}
        </p>
        <button
          onClick={() => window.history.back()}
          className="w-full sm:w-auto mt-2 px-6 py-2.5 bg-primary text-white rounded-md hover:bg-blue-600 transition-colors text-sm sm:text-base font-medium"
        >
          ← Go Back
        </button>
      </div>
    </div>
  </div>
);

const PrivateRoute = ({ element, roles, allowAccess, featureMessage }) => {
  const hasLocalSession = authService.isAuthenticated();
  const [rejected, setRejected] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (!hasLocalSession) return;

    authService.verifySession()
      .then((response) => {
        if (!response || !response.authenticated) {
          authService.logout();
          setRejected(true);
        }
      })
      .catch(() => {
        authService.logout();
        setRejected(true);
      });
  }, [hasLocalSession]);

  if (!hasLocalSession || rejected) {
    return <Navigate to="/login" replace />;
  }

  const user = authService.getCurrentUser();

  if (roles && roles.length > 0) {
    const hasRoleAccess = authService.hasAnyRole(roles);

    if (!hasRoleAccess) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-light p-4">
          <div className="text-center w-full">
            <div className="bg-white p-6 sm:p-8 rounded-lg shadow-lg max-w-md mx-auto">
              <div className="text-danger text-4xl sm:text-5xl mb-4">🚫</div>
              <p className="text-danger text-lg sm:text-xl font-semibold mb-2">Access Denied</p>
              <p className="text-secondary text-sm sm:text-base mt-2 mb-4">
                You do not have permission to access this page.
              </p>
              <div className="bg-gray-50 rounded-lg p-3 sm:p-4 mb-4 text-left">
                <p className="text-xs sm:text-sm text-secondary mb-1">
                  <span className="font-medium text-gray-700">Required role:</span> {roles.join(' or ')}
                </p>
                <p className="text-xs sm:text-sm text-secondary">
                  <span className="font-medium text-gray-700">Your role:</span> {user?.role || 'Unknown'}
                </p>
              </div>
              <button
                onClick={() => window.history.back()}
                className="w-full sm:w-auto mt-2 px-6 py-2.5 bg-primary text-white rounded-md hover:bg-blue-600 transition-colors text-sm sm:text-base font-medium"
              >
                ← Go Back
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  if (allowAccess && !allowAccess(user)) {
    return (
      <FeatureUnavailable message={featureMessage || 'This feature is not available for your company configuration.'} />
    );
  }

  const pathModule = moduleForPath(location.pathname);
  if (pathModule && !isRoleModuleEnabled(user, pathModule)) {
    return (
      <FeatureUnavailable message="This module is turned off for your role in Company Settings." />
    );
  }

  return element;
};

export default PrivateRoute;
